import axios from "axios";

const api = axios.create({
  baseURL: "/v1/api",
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("jwt_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Only set x-environment from localStorage if not already explicitly set per-request
  if (!config.headers["x-environment"]) {
    config.headers["x-environment"] = localStorage.getItem("selected_env") || "dev";
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("jwt_token");
      localStorage.removeItem("jwt_user");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  googleLogin: (code) => api.post("/auth/google", { code }),
  me: () => api.get("/auth/me"),
};

export const tenantApi = {
  getAll: () => api.get("/tenants"),
  getAllForEnv: (env) => api.get("/tenants", { headers: { "x-environment": env } }),
  getCopyTargetsForEnv: (env) => api.get("/tenants/copy-targets", { headers: { "x-environment": env } }),
};

export const configApi = {
  getConfig: (schema) =>
    api.get("/appconfig", { headers: { "x-tenant-schema": schema } }),
  createConfig: (schema, data) =>
    api.post("/appconfig", data, { headers: { "x-tenant-schema": schema } }),
  updateConfig: (schema, id, data) =>
    api.put(`/appconfig/${id}`, data, { headers: { "x-tenant-schema": schema } }),
  copyToEnvs: (data) =>
    api.post("/config-copy", data),
};

export const auditApi = {
  getLogs: (params) => api.get("/audit-logs", { params }),
};

export const envsApi = {
  getAvailable: () => api.get("/envs"),
};

export const userApi = {
  getAll: () => api.get("/users"),
  create: (data) => api.post("/users", data),
  update: (id, data) => api.put(`/users/${id}`, data),
  updateAccess: (id, data) => api.put(`/users/${id}/access`, data),
  getGlobalPermissions: () => api.get("/users/global-permissions"),
  updateGlobalPermissions: (id, data) => api.put(`/users/${id}/global-permissions`, data),
};

export const roleApi = {
  getAll: () => api.get("/roles"),
  getPermissions: () => api.get("/roles/permissions"),
  create: (data) => api.post("/roles", data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

export const adminApi = {
  getMigrateInfo: () => api.get("/admin/migrate-info"),
  migrateCentral: (data) => api.post("/admin/migrate-central", data),
};

export const aiAssistantApi = {
  // env is explicit — the sq-ai sandbox has its own env selector, separate
  // from the admin panel's global one.
  getTenantTerminology: (env, tenant) =>
    api.get("/ai-assistant/tenant-terminology", { params: { tenant }, headers: { "x-environment": env } }),
  // Context picker: the tenant's askable modules (labels + disabled resolved
  // server-side) and a module's currently-usable templates.
  getContextModules: (env, tenant) =>
    api.get("/ai-assistant/context/modules", { params: { tenant }, headers: { "x-environment": env } }),
  getContextTemplates: (env, tenant, module) =>
    api.get("/ai-assistant/context/templates", { params: { tenant, module }, headers: { "x-environment": env } }),
  // The org chip's tier definitions: active levels (depth) + per-tenant labels, by
  // position. Loaded once per tenant. Returns { levels: [{ key, position, label, active }] }.
  getOrgLevels: (env, tenant) =>
    api.get("/ai-assistant/context/org-levels", { params: { tenant }, headers: { "x-environment": env } }),
  // Cascading org-hierarchy options for the org chip: one tier at a time
  // (facility → department → location → subarea). `parentIds` is the selected parent
  // node id(s) for child tiers (omit for facility) — an array (multi-select) is sent as
  // a comma-joined list so children of ALL selected parents are unioned; a single id is
  // also accepted. Returns [{ id, label }]; the picker relabels each tier by position
  // from the tenant's org_hierarchy. Mirrors getContextTemplates' base + auth + env.
  getOrgHierarchy: (env, tenant, level, parentIds) => {
    const params = { tenant, level };
    if (Array.isArray(parentIds)) {
      if (parentIds.length) params.parentIds = parentIds.join(",");
    } else if (parentIds != null) {
      params.parentIds = parentIds;
    }
    return api.get("/ai-assistant/context/org-hierarchy", { params, headers: { "x-environment": env } });
  },
  // CSV data export (Phase 1). POST the export descriptor (from ai:done) back to the
  // engine; it re-runs the SAME query with the export row-cap and streams a CSV
  // attachment. responseType 'blob' so the body is the file; the row counts ride
  // response headers (X-Export-*). A plain <a href> can't carry the JWT, so this goes
  // through axios (Bearer + x-environment interceptor) and the caller triggers the
  // download from the blob. `rows` optionally overrides the cap (clamped server-side).
  exportData: (env, tenant, descriptor, rows) =>
    api.post(
      "/ai-assistant/export",
      { descriptor, format: "csv", rows },
      { params: { tenant }, headers: { "x-environment": env }, responseType: "blob" }
    ),
};

// The transcript the broker consumer persisted (server/controllers/chatEvents). Own-user only —
// the caller's identity comes from the token server-side, so the tenant is the only thing this
// surface chooses, and it names the Mongo collection the read opens.
//
// NO env parameter, unlike aiAssistantApi: the stored document does not record one, so passing it
// would imply a separation the store does not make.
export const chatHistoryApi = {
  // `cursor` is the previous response's `nextCursor`, passed back verbatim — it is opaque and must
  // never be parsed or built here. Omitting both paging params returns the newest page, which is
  // what the first load asks for.
  // `everyone` (RSM4; on or off the resume switch): every internal user's chats, each naming its `owner` — the "Mine only" box unticked.
  list: (tenant, { limit, cursor, everyone } = {}) =>
    api.get("/chat-events/history", { params: { tenant, ...(limit ? { limit } : {}), ...(cursor ? { cursor } : {}), ...(everyone ? { everyone: 1 } : {}) } }),
  // ⚠ THE TRANSCRIPT PAGES BACKWARDS: no cursor returns the NEWEST turns, and `nextCursor` fetches
  // the EARLIER ones. `limit` counts TURNS, never messages — a page never splits a question from
  // its answer.
  // `everyone` (RSM4): any internal user's chat, its first page naming its `owner` — the page continues only its own.
  get: (tenant, conversationId, { limit, cursor, everyone } = {}) =>
    api.get(`/chat-events/history/${encodeURIComponent(conversationId)}`, {
      params: { tenant, ...(limit ? { limit } : {}), ...(cursor ? { cursor } : {}), ...(everyone ? { everyone: 1 } : {}) },
    }),
};

// Credit usage for the signed-in user — the /usage dev page. env rides the x-environment header
// (the interceptor sends it), because unlike the transcript reads this one hits MSSQL and a
// tenant's database is resolved per environment.
// Live credit state for the dev billing panel — the reserve appears and disappears inside a single
// turn, which is exactly what a period total cannot show. Polled while the panel is open, one read at a time — hence the
// timeout: a read that never ended would stop the polling for good.
export const billingDebugApi = {
  get: (env, tenant) =>
    api.get("/chat-events/billing-debug", { params: { tenant }, headers: { "x-environment": env }, timeout: 20000 }),
};

export const usageApi = {
  get: (env, tenant) =>
    api.get("/chat-events/usage", { params: { tenant }, headers: { "x-environment": env } }),
};

export default api;
